/**
 * CL-391C — Client-uploaded project documents (pmProjectFiles metadata).
 */

import { CONTROLLED_DOCUMENT_STATUS } from "./controlledProjectDocuments";

export const CLIENT_DOCUMENT_LIBRARY_TYPE = "client_document";

export const CLIENT_REFERENCE_PHOTO_SUBCATEGORY = "reference";

export const CLIENT_REFERENCE_PHOTO_UPLOAD_SOURCE = "client_portal";

export const CLIENT_DOCUMENT_REQUEST_STATUS = Object.freeze({
  OPEN: "open",
  RECEIVED: "received",
  DISMISSED: "dismissed",
});

export const CLIENT_DOCUMENT_UPLOAD_SUCCESS_MSG =
  "Document uploaded successfully. Your project team can now see it.";

export const CLIENT_DOCUMENT_EMPTY_CLIENT =
  "No documents uploaded yet. Use the form below when your project team requests files.";

export const OWNER_DOCUMENT_EMPTY_HISTORICAL_CLIENT =
  "No owner documents uploaded yet. Use the form below to store property-related files.";

export const OWNER_DOCUMENT_HISTORICAL_SUBTITLE =
  "Store surveys, permits, appraisals, engineering reports, property records, photos, and other documents related to this property.";

export const CLIENT_DOCUMENT_ACCEPTED_EXTENSIONS = Object.freeze([
  ".pdf",
  ".jpg",
  ".jpeg",
  ".png",
  ".docx",
  ".xlsx",
]);

export const CLIENT_DOCUMENT_ACCEPTED_MIME_TYPES = Object.freeze([
  "application/pdf",
  "image/jpeg",
  "image/jpg",
  "image/png",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
]);

function safeStr(value) {
  return String(value ?? "").trim();
}

function toTimeMs(value) {
  if (!value) return 0;
  if (typeof value?.toDate === "function") {
    const d = value.toDate();
    return d instanceof Date && !Number.isNaN(d.getTime()) ? d.getTime() : 0;
  }
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? 0 : value.getTime();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

export function isClientProjectDocument(file) {
  return safeStr(file?.libraryType).toLowerCase() === CLIENT_DOCUMENT_LIBRARY_TYPE;
}

export function isClientReferencePhotoUpload(file) {
  if (!file) return false;
  const mime = safeStr(file.type).toLowerCase();
  if (mime.startsWith("image/")) return true;
  const name = safeStr(file.name).toLowerCase();
  return [".jpg", ".jpeg", ".png"].some((ext) => name.endsWith(ext));
}

export function isClientUploadedReferencePhoto(file) {
  if (!file || safeStr(file?.status).toLowerCase() === "deleted") return false;
  if (safeStr(file?.libraryType).toLowerCase() !== "photo") return false;
  if (safeStr(file?.subcategory).toLowerCase() === CLIENT_REFERENCE_PHOTO_SUBCATEGORY) return true;
  if (safeStr(file?.clientUploadSource).toLowerCase() === CLIENT_REFERENCE_PHOTO_UPLOAD_SOURCE) return true;
  return false;
}

export function isClientPortalUploadFile(file) {
  return isActiveClientProjectDocument(file) || isClientUploadedReferencePhoto(file);
}

export function isActiveClientProjectDocument(file) {
  if (!isClientProjectDocument(file)) return false;
  if (safeStr(file?.status).toLowerCase() === "deleted") return false;
  return safeStr(file?.documentStatus).toLowerCase() !== CONTROLLED_DOCUMENT_STATUS.ARCHIVED;
}

export function getClientDocumentDisplayTitle(file) {
  return safeStr(file?.documentDisplayTitle) || safeStr(file?.name) || "Document";
}

export function getClientDocumentsRequestNote(project) {
  return safeStr(project?.clientDocumentsRequestNote) || "";
}

function newClientDocumentRequestItemId() {
  return `cdr_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function normalizeRequestStatus(value) {
  const key = safeStr(value).toLowerCase();
  if (key === CLIENT_DOCUMENT_REQUEST_STATUS.RECEIVED) return CLIENT_DOCUMENT_REQUEST_STATUS.RECEIVED;
  if (key === CLIENT_DOCUMENT_REQUEST_STATUS.DISMISSED) return CLIENT_DOCUMENT_REQUEST_STATUS.DISMISSED;
  return CLIENT_DOCUMENT_REQUEST_STATUS.OPEN;
}

/** CL-391E-FIX1 — Firestore arrays cannot contain serverTimestamp(); use ISO strings. */
export function nowRequestItemTimestamp() {
  return new Date().toISOString();
}

function toSerializableRequestTimestamp(value) {
  if (!value) return null;
  if (typeof value === "string") return value;
  if (typeof value?.toDate === "function") {
    const d = value.toDate();
    return d instanceof Date && !Number.isNaN(d.getTime()) ? d.toISOString() : null;
  }
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.toISOString();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function formatClientDocumentRequestTimestamp(value) {
  const ms = toTimeMs(value);
  if (!ms) return "";
  try {
    return new Date(ms).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

export function sortClientDocumentRequestItemsByCreatedAt(items = []) {
  return [...(items || [])].sort((a, b) => {
    const bms = toTimeMs(b?.createdAt);
    const ams = toTimeMs(a?.createdAt);
    if (bms !== ams) return bms - ams;
    return safeStr(a?.title).localeCompare(safeStr(b?.title));
  });
}

/** @returns {object|null} */
export function normalizeClientDocumentRequestItem(raw, index = 0) {
  if (raw == null) return null;
  if (typeof raw === "string") {
    const title = safeStr(raw);
    if (!title) return null;
    return {
      id: `legacy-${index}-${title.slice(0, 24).replace(/\s+/g, "_")}`,
      title,
      note: null,
      status: CLIENT_DOCUMENT_REQUEST_STATUS.OPEN,
      createdAt: null,
      createdByUid: null,
      createdByEmail: null,
      dismissedAt: null,
    };
  }
  if (typeof raw === "object") {
    const title = safeStr(raw.title);
    if (!title) return null;
    return {
      id: safeStr(raw.id) || `item-${index}`,
      title,
      note: safeStr(raw.note) || null,
      status: normalizeRequestStatus(raw.status),
      createdAt: toSerializableRequestTimestamp(raw.createdAt),
      createdByUid: safeStr(raw.createdByUid) || null,
      createdByEmail: safeStr(raw.createdByEmail) || null,
      receivedAt: toSerializableRequestTimestamp(raw.receivedAt),
      receivedByUid: safeStr(raw.receivedByUid) || null,
      receivedByEmail: safeStr(raw.receivedByEmail) || null,
      receivedFileId: safeStr(raw.receivedFileId) || null,
      receivedFileTitle: safeStr(raw.receivedFileTitle) || null,
      dismissedAt: toSerializableRequestTimestamp(raw.dismissedAt),
      dismissedByUid: safeStr(raw.dismissedByUid) || null,
      dismissedByEmail: safeStr(raw.dismissedByEmail) || null,
    };
  }
  return null;
}

export function normalizeClientDocumentsRequestItems(project) {
  const raw = project?.clientDocumentsRequestItems;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item, index) => normalizeClientDocumentRequestItem(item, index))
    .filter(Boolean);
}

/** @deprecated Prefer normalizeClientDocumentsRequestItems — returns title strings for legacy callers. */
export function getClientDocumentsRequestItems(project) {
  return normalizeClientDocumentsRequestItems(project).map((item) => item.title);
}

export function getOpenClientDocumentRequestItems(project) {
  return normalizeClientDocumentsRequestItems(project).filter(
    (item) => item.status === CLIENT_DOCUMENT_REQUEST_STATUS.OPEN
  );
}

export function getReceivedClientDocumentRequestItems(project) {
  return normalizeClientDocumentsRequestItems(project).filter(
    (item) => item.status === CLIENT_DOCUMENT_REQUEST_STATUS.RECEIVED
  );
}

export function getDismissedClientDocumentRequestItems(project) {
  return normalizeClientDocumentsRequestItems(project).filter(
    (item) => item.status === CLIENT_DOCUMENT_REQUEST_STATUS.DISMISSED
  );
}

export function countOpenClientDocumentRequests(project) {
  return getOpenClientDocumentRequestItems(project).length;
}

export function documentRequestUploadLabel(count) {
  const n = Math.max(0, Number(count) || 0);
  if (n === 0) return null;
  if (n === 1) return "Upload 1 requested document";
  return `Upload ${n} requested documents`;
}

export function documentRequestPendingDashboardLabel(count) {
  const n = Math.max(0, Number(count) || 0);
  if (n === 0) return null;
  if (n === 1) return "1 document request pending";
  return `${n} document requests pending`;
}

export function getClientDocumentRequestStatusLabel(status) {
  const key = normalizeRequestStatus(status);
  if (key === CLIENT_DOCUMENT_REQUEST_STATUS.RECEIVED) return "Received";
  if (key === CLIENT_DOCUMENT_REQUEST_STATUS.DISMISSED) return "Dismissed";
  return "Open";
}

export function buildNewClientDocumentRequestItem({ title, note = "", uid = null, email = null } = {}) {
  return {
    id: newClientDocumentRequestItemId(),
    title: safeStr(title),
    note: safeStr(note) || null,
    status: CLIENT_DOCUMENT_REQUEST_STATUS.OPEN,
    createdAt: nowRequestItemTimestamp(),
    createdByUid: safeStr(uid) || null,
    createdByEmail: safeStr(email) || null,
    receivedAt: null,
    receivedByUid: null,
    receivedByEmail: null,
    receivedFileId: null,
    receivedFileTitle: null,
    dismissedAt: null,
    dismissedByUid: null,
    dismissedByEmail: null,
  };
}

export function buildDismissedClientDocumentRequestItemPatch({ uid, email } = {}) {
  return {
    status: CLIENT_DOCUMENT_REQUEST_STATUS.DISMISSED,
    dismissedAt: nowRequestItemTimestamp(),
    dismissedByUid: safeStr(uid) || null,
    dismissedByEmail: safeStr(email) || null,
  };
}

export function buildReceivedClientDocumentRequestItemPatch({
  uid,
  email,
  fileId = null,
  fileTitle = null,
} = {}) {
  return {
    status: CLIENT_DOCUMENT_REQUEST_STATUS.RECEIVED,
    receivedAt: nowRequestItemTimestamp(),
    receivedByUid: safeStr(uid) || null,
    receivedByEmail: safeStr(email) || null,
    receivedFileId: safeStr(fileId) || null,
    receivedFileTitle: safeStr(fileTitle) || null,
    dismissedAt: null,
    dismissedByUid: null,
    dismissedByEmail: null,
  };
}

export function buildReopenClientDocumentRequestItemPatch() {
  return {
    status: CLIENT_DOCUMENT_REQUEST_STATUS.OPEN,
    receivedAt: null,
    receivedByUid: null,
    receivedByEmail: null,
    receivedFileId: null,
    receivedFileTitle: null,
    dismissedAt: null,
    dismissedByUid: null,
    dismissedByEmail: null,
  };
}

export function buildClientDocumentRequestLinkPatch({ requestId, requestTitle } = {}) {
  const rid = safeStr(requestId);
  return {
    clientDocumentRequestId: rid || null,
    clientDocumentRequestTitle: rid ? safeStr(requestTitle) || null : null,
  };
}

export function isAdditionalClientDocument(file) {
  // CL-PORTAL-FILES-001: photos belong in Photos, not Documents additional list.
  if (isClientUploadedReferencePhoto(file)) return false;
  if (safeStr(file?.libraryType).toLowerCase() === "photo") return false;
  return isClientProjectDocument(file) && !safeStr(file?.clientDocumentRequestId);
}

export function filterAdditionalClientDocuments(files = []) {
  return filterClientDocumentsForClientPortal(files).filter((file) => isAdditionalClientDocument(file));
}

export function findClientDocumentForRequest(files = [], requestId) {
  const rid = safeStr(requestId);
  if (!rid) return null;
  return (
    filterClientDocumentsForClientPortal(files).find(
      (file) => safeStr(file?.clientDocumentRequestId) === rid
    ) || null
  );
}

export function applyRequestFulfillmentToItems(items, requestId, { fileId, fileTitle, uid, email } = {}) {
  const rid = safeStr(requestId);
  return (items || []).map((item) =>
    item.id === rid
      ? {
          ...item,
          ...buildReceivedClientDocumentRequestItemPatch({
            uid,
            email,
            fileId,
            fileTitle,
          }),
        }
      : item
  );
}

/**
 * CL-391F — Client portal fulfillment sections (open / completed / additional).
 */
export function buildClientDocumentRequestFulfillmentModel(project, files = []) {
  const allItems = normalizeClientDocumentsRequestItems(project);
  const openItems = allItems.filter((item) => item.status === CLIENT_DOCUMENT_REQUEST_STATUS.OPEN);
  const receivedItems = allItems.filter((item) => item.status === CLIENT_DOCUMENT_REQUEST_STATUS.RECEIVED);
  const requestNote = getClientDocumentsRequestNote(project);
  const additionalFiles = sortClientDocumentsByUploadDate(filterAdditionalClientDocuments(files));
  const receivedCount = filterClientDocumentsForClientPortal(files).length;

  const completedItems = receivedItems.map((item) => {
    const linkedFile =
      findClientDocumentForRequest(files, item.id) ||
      (item.receivedFileId
        ? (files || []).find((f) => f.id === item.receivedFileId) || null
        : null);
    return {
      ...item,
      linkedFile,
      linkedFileTitle:
        getClientDocumentDisplayTitle(linkedFile) ||
        safeStr(item.receivedFileTitle) ||
        null,
    };
  });

  const allOpenFulfilled = openItems.length === 0 && receivedItems.length > 0;
  const hasOpenRequests = openItems.length > 0;
  const sectionTone = hasOpenRequests ? "waiting" : allOpenFulfilled ? "complete" : "neutral";

  return {
    hasRequestWorkflow: openItems.length > 0 || receivedItems.length > 0,
    openItems,
    completedItems,
    additionalFiles,
    requestNote: hasOpenRequests || receivedItems.length > 0 ? requestNote : "",
    openRequestCount: openItems.length,
    receivedCount,
    sectionTone,
    allOpenFulfilled,
  };
}

export function buildPendingDocumentRequestDashboardRows(projects = []) {
  return (projects || [])
    .map((project) => {
      const pendingCount = countOpenClientDocumentRequests(project);
      if (!pendingCount) return null;
      return {
        projectId: safeStr(project.id),
        projectName: safeStr(project.name || project.title || "Project"),
        pendingCount,
        label: documentRequestPendingDashboardLabel(pendingCount),
        ctaLabel: "Upload documents",
      };
    })
    .filter(Boolean);
}

const ADMIN_CLIENT_DOC_RECENT_UPLOAD_DAYS = 14;

/**
 * CL-392A — Client project page attention rows (drawings + document requests).
 */
export function buildClientProjectAttentionModel({
  project,
  files = [],
  pendingReviewCount = 0,
} = {}) {
  const fulfillment = buildClientDocumentRequestFulfillmentModel(project, files);
  const items = [];

  const reviewCount = Math.max(0, Number(pendingReviewCount) || 0);
  if (reviewCount > 0) {
    items.push({
      id: "drawings-review",
      label: reviewCount === 1 ? "1 drawing awaiting review" : `${reviewCount} drawings awaiting review`,
      tone: "waiting",
      ctaLabel: "Review",
      scrollTarget: "client-drawings-section",
    });
  }

  if (fulfillment.openRequestCount > 0) {
    items.push({
      id: "doc-requests",
      label: documentRequestPendingDashboardLabel(fulfillment.openRequestCount),
      tone: "waiting",
      ctaLabel: "Upload documents",
      scrollTarget: "client-documents-section",
    });
  }

  if (fulfillment.completedItems.length > 0) {
    items.push({
      id: "doc-received",
      label:
        fulfillment.completedItems.length === 1
          ? "1 requested document received"
          : `${fulfillment.completedItems.length} requested documents received`,
      tone: "complete",
      ctaLabel: "View documents",
      scrollTarget: "client-documents-section",
    });
  }

  const hasWaiting = items.some((item) => item.tone === "waiting");

  return {
    items,
    hasAttention: hasWaiting,
    sectionTone: fulfillment.sectionTone,
    openRequestCount: fulfillment.openRequestCount,
    completedRequestCount: fulfillment.completedItems.length,
  };
}

/**
 * CL-392A — Admin dashboard aggregate for client document attention.
 */
export function buildAdminClientDocumentAttentionSummary(projects = [], clientDocFiles = []) {
  const projectList = Array.isArray(projects) ? projects : [];
  const files = Array.isArray(clientDocFiles) ? clientDocFiles : [];
  const cutoffMs = Date.now() - ADMIN_CLIENT_DOC_RECENT_UPLOAD_DAYS * 86400000;

  let openRequestCount = 0;
  let deleteRequestCount = 0;
  let recentUploadCount = 0;

  const projectSignals = new Map();

  for (const project of projectList) {
    const pid = safeStr(project?.id);
    if (!pid) continue;
    const open = countOpenClientDocumentRequests(project);
    if (open > 0) {
      openRequestCount += open;
      const row = projectSignals.get(pid) || {
        projectId: pid,
        projectName: safeStr(project.name || project.title || "Project"),
        score: 0,
      };
      row.openRequestCount = open;
      row.score += open * 3;
      projectSignals.set(pid, row);
    }
  }

  for (const file of files) {
    if (!isActiveClientProjectDocument(file)) continue;
    const pid = safeStr(file?.projectId);
    if (!pid) continue;

    if (file.deleteRequested === true) {
      deleteRequestCount += 1;
      const row = projectSignals.get(pid) || {
        projectId: pid,
        projectName: safeStr(file.projectName || "Project"),
        score: 0,
      };
      row.deleteRequestCount = (row.deleteRequestCount || 0) + 1;
      row.score += 5;
      projectSignals.set(pid, row);
    }

    const ms = toTimeMs(file.createdAt) || toTimeMs(file.updatedAt);
    if (ms >= cutoffMs) {
      recentUploadCount += 1;
      const row = projectSignals.get(pid) || {
        projectId: pid,
        projectName: safeStr(file.projectName || "Project"),
        score: 0,
      };
      row.recentUploadCount = (row.recentUploadCount || 0) + 1;
      row.score += 2;
      projectSignals.set(pid, row);
    }
  }

  const rankedProjects = [...projectSignals.values()].sort((a, b) => b.score - a.score);
  const primaryProject = rankedProjects[0] || null;
  const totalAttentionCount = recentUploadCount + deleteRequestCount + openRequestCount;

  return {
    openRequestCount,
    deleteRequestCount,
    recentUploadCount,
    totalAttentionCount,
    primaryProjectId: primaryProject?.projectId || null,
    primaryProjectName: primaryProject?.projectName || null,
    rankedProjects,
    reviewCtaLabel: "Review client documents",
  };
}

/**
 * CL-392A — Admin project overview snapshot for client documents.
 */
export function buildAdminProjectClientDocumentsAttentionModel(project, files = []) {
  const fulfillment = buildClientDocumentRequestFulfillmentModel(project, files);
  const deleteRequestCount = countClientDocumentsWithDeleteRequested(files);
  const activeUploadCount = filterClientDocumentsForAdmin(files).filter(
    (f) => safeStr(f?.documentStatus).toLowerCase() !== CONTROLLED_DOCUMENT_STATUS.ARCHIVED
  ).length;

  const hasAttention =
    fulfillment.openRequestCount > 0 || deleteRequestCount > 0 || fulfillment.receivedCount > 0;

  let tone = "neutral";
  if (fulfillment.openRequestCount > 0 || deleteRequestCount > 0) tone = "waiting";
  else if (fulfillment.allOpenFulfilled && fulfillment.receivedCount > 0) tone = "complete";

  return {
    hasAttention,
    tone,
    openRequestCount: fulfillment.openRequestCount,
    receivedRequestCount: fulfillment.completedItems.length,
    deleteRequestCount,
    activeUploadCount,
    reviewCtaLabel: "Review client documents",
  };
}

export function serializeClientDocumentsRequestItemsForFirestore(items = []) {
  return (items || [])
    .map((item) => normalizeClientDocumentRequestItem(item))
    .filter(Boolean)
    .map((item) => ({
      id: item.id,
      title: item.title,
      note: item.note,
      status: item.status,
      createdAt: toSerializableRequestTimestamp(item.createdAt),
      createdByUid: item.createdByUid,
      createdByEmail: item.createdByEmail,
      receivedAt: toSerializableRequestTimestamp(item.receivedAt),
      receivedByUid: item.receivedByUid,
      receivedByEmail: item.receivedByEmail,
      receivedFileId: item.receivedFileId,
      receivedFileTitle: item.receivedFileTitle,
      dismissedAt: toSerializableRequestTimestamp(item.dismissedAt),
      dismissedByUid: item.dismissedByUid,
      dismissedByEmail: item.dismissedByEmail,
    }));
}

/**
 * @param {object} project
 * @param {object[]} files
 * @param {{ audience?: 'client'|'admin' }} [options]
 */
export function buildClientDocumentsRequestModel(project, files = [], options = {}) {
  const audience = options.audience === "admin" ? "admin" : "client";
  const allItems = normalizeClientDocumentsRequestItems(project);
  const openItems = allItems.filter((item) => item.status === CLIENT_DOCUMENT_REQUEST_STATUS.OPEN);
  const hasRequest = openItems.length > 0;
  const requestNote = hasRequest || allItems.some((i) => i.status === CLIENT_DOCUMENT_REQUEST_STATUS.RECEIVED)
    ? getClientDocumentsRequestNote(project)
    : "";

  const receivedFiles =
    audience === "admin"
      ? filterClientDocumentsForAdmin(files).filter(
          (f) => safeStr(f?.documentStatus).toLowerCase() !== CONTROLLED_DOCUMENT_STATUS.ARCHIVED
        )
      : filterClientDocumentsForClientPortal(files);

  const receivedCount = receivedFiles.length;
  const allRequestsFulfilled = openItems.length === 0 && allItems.some((i) => i.status === CLIENT_DOCUMENT_REQUEST_STATUS.RECEIVED);

  if (!hasRequest && !allRequestsFulfilled) {
    return {
      hasRequest: false,
      status: null,
      receivedCount,
      requestNote: "",
      requestItems: [],
      openRequestCount: 0,
      sectionTone: "neutral",
    };
  }

  const status = hasRequest ? "waiting" : allRequestsFulfilled && receivedCount > 0 ? "received" : "waiting";

  return {
    hasRequest: hasRequest || allRequestsFulfilled,
    status,
    receivedCount,
    requestNote,
    requestItems: audience === "admin" ? allItems.filter((i) => i.status !== CLIENT_DOCUMENT_REQUEST_STATUS.DISMISSED) : openItems,
    openRequestCount: openItems.length,
    sectionTone: hasRequest ? "waiting" : allRequestsFulfilled ? "complete" : "neutral",
  };
}

/** CL-391C/D-FIX1 — Storage path allowed by storage.rules clientUploads rule. */
export function buildClientDocumentStoragePath(projectId, uid, filename) {
  const pid = safeStr(projectId);
  const ownerUid = safeStr(uid);
  const name = safeStr(filename) || "file";
  return `pmProjectFiles/${pid}/clientUploads/${ownerUid}/${name}`;
}

/**
 * @param {object[]} files
 */
export function filterClientDocumentsForAdmin(files = []) {
  return (files || []).filter((file) => {
    if (!isClientProjectDocument(file)) return false;
    if (safeStr(file?.status).toLowerCase() === "deleted") return false;
    return true;
  });
}

/**
 * Active client documents visible on the Documents & requests upload area.
 * CL-PORTAL-FILES-001: project photos (libraryType photo / client reference JPG/PNG)
 * belong only in Photos — never dual-render here.
 * @param {object[]} files
 */
export function filterClientDocumentsForClientPortal(files = []) {
  return (files || []).filter((file) => {
    if (file?.clientVisible === false) return false;
    // Photos tab owns these rows (domain: project_photo).
    if (isClientUploadedReferencePhoto(file)) return false;
    if (safeStr(file?.libraryType).toLowerCase() === "photo") return false;
    return isActiveClientProjectDocument(file);
  });
}

export function sortClientDocumentsByUploadDate(files = []) {
  return [...(files || [])].sort((a, b) => {
    const bms = Math.max(toTimeMs(b?.createdAt), toTimeMs(b?.updatedAt));
    const ams = Math.max(toTimeMs(a?.createdAt), toTimeMs(a?.updatedAt));
    if (bms !== ams) return bms - ams;
    return getClientDocumentDisplayTitle(a).localeCompare(getClientDocumentDisplayTitle(b));
  });
}

export function formatClientDocumentUploadDate(file) {
  const ms = toTimeMs(file?.createdAt) || toTimeMs(file?.updatedAt);
  if (!ms) return "";
  try {
    return new Date(ms).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

/**
 * @param {File} file
 */
export function isAcceptedClientDocumentUpload(file) {
  if (!file) return false;
  const mime = safeStr(file.type).toLowerCase();
  if (CLIENT_DOCUMENT_ACCEPTED_MIME_TYPES.includes(mime)) return true;
  const name = safeStr(file.name).toLowerCase();
  return CLIENT_DOCUMENT_ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));
}

export function isClientDocumentOwnedByUser(file, uid) {
  return Boolean(safeStr(uid)) && safeStr(file?.uploadedByUid) === safeStr(uid);
}

export function countClientDocumentsWithDeleteRequested(files = []) {
  return filterClientDocumentsForAdmin(files).filter((f) => f.deleteRequested === true).length;
}

export function buildClientDocumentDeleteRequestPatch({ uid, email } = {}) {
  return {
    deleteRequested: true,
    deleteRequestedByUid: safeStr(uid) || null,
    deleteRequestedByEmail: safeStr(email) || null,
  };
}

export function buildClientDocumentDeleteRequestCancelPatch({ uid, email } = {}) {
  return {
    deleteRequested: false,
    deleteRequestCancelledByUid: safeStr(uid) || null,
    deleteRequestCancelledByEmail: safeStr(email) || null,
  };
}

export function buildClientDocumentMetadataPatch({ displayTitle, description } = {}) {
  return {
    documentDisplayTitle: safeStr(displayTitle) || null,
    documentDescription: safeStr(description) || null,
  };
}

export function buildClientDocumentFirestorePatch({
  displayTitle,
  description = "",
  uploadedByUid = null,
  uploadedByEmail = null,
  clientVisible = true,
  documentStatus = CONTROLLED_DOCUMENT_STATUS.ACTIVE,
  clientDocumentRequestId = null,
  clientDocumentRequestTitle = null,
} = {}) {
  return {
    libraryType: CLIENT_DOCUMENT_LIBRARY_TYPE,
    documentDisplayTitle: safeStr(displayTitle) || null,
    documentDescription: safeStr(description) || null,
    clientVisible: clientVisible !== false,
    documentStatus: safeStr(documentStatus).toLowerCase() || CONTROLLED_DOCUMENT_STATUS.ACTIVE,
    uploadedByUid: safeStr(uploadedByUid) || null,
    uploadedByEmail: safeStr(uploadedByEmail) || null,
    ...buildClientDocumentRequestLinkPatch({
      requestId: clientDocumentRequestId,
      requestTitle: clientDocumentRequestTitle,
    }),
  };
}

export function buildClientReferencePhotoFirestorePatch({
  displayTitle,
  description = "",
  uploadedByUid = null,
  uploadedByEmail = null,
  clientDocumentRequestId = null,
  clientDocumentRequestTitle = null,
} = {}) {
  return {
    libraryType: "photo",
    category: "photos",
    subcategory: CLIENT_REFERENCE_PHOTO_SUBCATEGORY,
    clientUploadSource: CLIENT_REFERENCE_PHOTO_UPLOAD_SOURCE,
    documentDisplayTitle: safeStr(displayTitle) || null,
    documentDescription: safeStr(description) || null,
    clientVisible: true,
    uploadedByUid: safeStr(uploadedByUid) || null,
    uploadedByEmail: safeStr(uploadedByEmail) || null,
    ...buildClientDocumentRequestLinkPatch({
      requestId: clientDocumentRequestId,
      requestTitle: clientDocumentRequestTitle,
    }),
  };
}

/**
 * Route client portal uploads: images become planning reference photos; other files stay client documents.
 * @param {File} file
 */
export function buildClientUploadFirestorePatch(file, options = {}) {
  if (isClientReferencePhotoUpload(file)) {
    return buildClientReferencePhotoFirestorePatch(options);
  }
  return buildClientDocumentFirestorePatch(options);
}
